import { Component, Inject, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { GlobalService } from 'app/global/global.service';
import { DateConversationPipe } from 'app/main/pages/dashboard/date-conversation.pipe';
import { ContractControlEmitterService } from 'app/main/pages/events/contract-control-emitter.service';
import { lastValueFrom } from 'rxjs';
import { ICCGroupManagerDDLItem } from '../../../documents/document-right-sidebar/add-to-contract-control-modal/managers/managers.component';
import { ITodoItemDTO, TodoUser } from '../../contract-control-interfaces/cc-activity.interface';
import { ContractControlService } from '../../contract-control.service';
import { Sectionable } from '../../store/dto/manager-list.dto';
import { contractControlConstant } from '../../contract-control-detail-view/contract-control-services/contract-control-constant.service';
import { CONTRACT_DETAIL_FACADE_TOKEN, IContractDetailFacade } from '../../contract-control-detail-view/store/contract-detail-facade.token';

interface ITodoForm {
  id: number,
  contract_control_id: number,
  description: string,
  due_date: string,
  status: number,
  type: string,
  users: TodoUser[]
}


@Component({
  selector: 'app-contract-control-todo-modal',
  templateUrl: './contract-control-todo-modal.component.html',
  styleUrls: ['./contract-control-todo-modal.component.scss'],
  encapsulation: ViewEncapsulation.None
})

export class ContractControlTodoModalComponent implements OnInit {

  @Input() todo: ITodoItemDTO;
  @Input() document_type_id: number;
  @Input() hasPermission: boolean;

  public todoForm: FormGroup;
  public submitted: boolean = false;
  public loading: boolean = false;
  public searchManagerLoading = false;
  public managers$ = this._contractFacade.managersList$;
  public managersAndGroups = [];
  public keyword: string = 'name';
  public isCollapsed: boolean = true;

  /**
   * Contructor
   * @param formBuilder
   * @param globalService
   * @param contractControlService
   * @param eventService
   * @param auditTrailService
   * @param activeModal
   * @param contractControlEmitter
   * @param _contractControlFacade
   * @param _contractControlConstant
   * @param _translocoService
   * @param _dateConversationPipe$
   * @param permissionService$
   */
  constructor(
    private formBuilder: FormBuilder,
    private globalService: GlobalService,
    private contractControlService: ContractControlService,
    public activeModal: NgbActiveModal,
    private contractControlEmitter: ContractControlEmitterService,
    private _contractControlConstant: contractControlConstant,
    private _translocoService: TranslocoService,
    private _dateConversationPipe$: DateConversationPipe,
    @Inject(CONTRACT_DETAIL_FACADE_TOKEN) public _contractFacade: IContractDetailFacade
  ) { }

  ngOnInit(): void {
    let todo: ITodoItemDTO;
    if (this.todo) {
      todo = this._contractControlConstant._deepCopy(this.todo);
      if (todo?.todo_user?.length) {
        todo?.todo_user.map((item) => {
          item.type = item?.userable?.default ? 'groups' : 'users';
          return item;
        });
      }
    }
    this.initTodoForm(todo);
  }


  /**
   * Initialize todo form
   * @param data 
   */
  initTodoForm(data: ITodoItemDTO) {
    this.todoForm = this.formBuilder.group({
      id: [data.id],
      contract_control_id: [data.contract_control_id],
      description: [data.description, [Validators.required]],
      due_date: [data.due_date ?? null],
      status: [data?.status ?? '0'],
      type: [''],
      users: [data.todo_user ?? []],
    });
  }




  /**
   * Adds a user or group to the `users` array in the form if it does not already exist.
   *
   * Determines whether the provided `data` represents a user or a group based on the `default` property.
   * Checks if the user/group is already present in the form's `users` array by matching the `id`.
   * If not present, constructs a user/group object with appropriate properties and pushes it to the `users` array.
   * The object structure depends on whether `tForm.id.value` is truthy:
   * - If truthy, uses `userable_id` as the identifier.
   * - Otherwise, uses `user_id` as the identifier.
   *
   * @param data - The user or group object to be added, conforming to the `Sectionable` interface.
   */
  patchUsers(data: Sectionable): void {
    const type = data?.default ? 'groups' : 'users';
    const exists = this.todoForm.value.users.some(item => item.id == data.id);
    if (!exists) {
      let result = {};
      if (this.tForm.id.value) {
        result = {
          userable_id: data.id, type: type,
          userable: data
        }
      } else {
        result = {
          user_id: data.id, type: type,
          userable: data
        }
      }
      this.tForm.users?.value.push(result);
    }
  }



  /**
   * Removes a user from the `users` array in the `todoForm` at the specified index.
   *
   * @param index - The index of the user to remove from the `users` array.
   */
  removeSelectedUser(index: number): void {
    const users = this.todoForm.value.users.filter((v, i) => i != index);
    this.todoForm.patchValue({ users: users });
  }




  /**
   * Handles the due date change event, transforms the selected date using a date conversion pipe,
   * and updates the `due_date` field in the todo form with the formatted date.
   *
   * @param $event - The event object from the date input field.
   * @returns A promise that resolves when the due date has been processed and the form updated.
   */
  async dueDate($event): Promise<void> {
    const formatedDate = await lastValueFrom(this._dateConversationPipe$.transform($event.target?.value, 'date'));
    this.todoForm.patchValue({ due_date: formatedDate })
  }



  submitTodo() {
    if (this.todoForm.get('id')?.value) {
      this.updateTodo();
    } else {
      this.createTodo();
    }
  }



  /** Get Todo form controls */
  get tForm() {
    return this.todoForm.controls;
  }



  /**
   * Handles the creation of a new todo item.
   * 
   * This method validates the todo form, sets loading and submission states,
   * and calls the contract control service to create a new todo. Upon success,
   * it logs activities, refreshes the todo list, closes the modal, and shows a success toast.
   * If an error occurs, it resets the submission and loading states and displays an error toast.
   *
   * @returns {void}
   */
  createTodo(): void {
    this.submitted = true;
    if (this.todoForm.invalid) {
      return;
    }
    this.loading = true;
    const payload = this.buildTodoPayload();
    this.contractControlService.createTodo(payload).subscribe({
      next: (response) => {
        this._contractFacade.getContractControlTodos(this.todo?.contract_control_id);
        this.closeModalWithEmitter();
        this.globalService.toastrSuccess(this._translocoService.translate('Create activity success toast message - (Contract control section) - Activity has been created'));
        this.globalService.toastrSuccess(response?.audit_message, {enableHtml: true});
      },

      error: (error) => {
        this.submitted = false;
        this.loading = false;
        this.globalService.toastrError(error);
      }
    });
  }





  /**
   * Updates the current todo item using the form values.
   *
   * - Marks the form as submitted.
   * - If the form is invalid, the update is aborted.
   * - Sets the loading state to true while the update is in progress.
   * - Calls the contract control service to update the todo item.
   * - On success:
   *   - Creates a todo activity log with the updated description.
   *   - Shows a success toast notification.
   *   - Refreshes the list of contract control todos.
   *   - Closes the modal and emits the close event.
   * - On error:
   *   - Displays an error toast notification with the error message.
   *   - Sets the loading state to false and the submitted state to false.
   */
  updateTodo(): void {
    this.submitted = true;
    if (this.todoForm.invalid) {
      return;
    }
    this.loading = true;
    const payload = this.buildTodoPayload();
    this.contractControlService.updateTodo(payload).subscribe({
      next: (response) => {
        this.globalService.toastrSuccess(this._translocoService.translate('Update activity success toast message - (Contract control section) - Activity has been updated'));
        this.globalService.toastrSuccess(response?.audit_message, {enableHtml: true});
        this._contractFacade.getContractControlTodos(this.todo?.contract_control_id);
        this.closeModalWithEmitter();
      },
      error: (error) => {
        this.globalService.toastrError(error);
        this.loading = false;
        this.submitted = false;
      }
    });
  }


  /**
   * Builds the payload for todo create/update API calls.
   * Transforms users array to match backend validation requirements.
   */
  private buildTodoPayload(): Partial<ITodoItemDTO> {
    const formValue = this.todoForm.value;
    return {
      ...formValue,
      users: formValue.users?.map(user => ({
        type: user.type,
        user_id: user.user_id ?? user.userable_id
      })) ?? []
    };
  }




  isNewAssigneeAdded(formValue) {
    return JSON.stringify(this.todo?.todo_user) === JSON.stringify(formValue?.users);
  }





  /**
   * Closes the modal dialog, resets the form and internal state, and triggers an update event.
   *
   * This method performs the following actions:
   * - Sets `loading` and `submitted` flags to `false`.
   * - Closes the active modal, passing `null` as the result.
   * - Resets the `todoForm` form group to its initial state.
   * - Emits an event via `contractControlEmitter` to trigger an update of the upcoming graph, 
   *   passing an object with `{ reinit: 1 }` as the payload.
   * 
   * @returns {void}
   */
  closeModalWithEmitter(): void {
    this.loading = false;
    this.submitted = false;
    this.activeModal.close(null);
    this.todoForm.reset();
    this.contractControlEmitter.triggerUpComingGraph({ reinit: 1 });
  }






  /**
   * Converts an array of user objects to a comma-separated string of user names.
   *
   * @param users - An array of user objects, each expected to have a `userable` property with a `name` field.
   * @returns A comma-separated string of user names, or an empty string if the array is empty.
   */
  arrayToCommanSeperate(users = []) {
    return (users.length) ? Array.prototype.map.call(users, item => item.userable.name).toString() : '';
  }




  /**
   * Searches for managers based on the provided query string.
   * Initiates a loading state, calls the contract control service to fetch managers,
   * and updates the managers and groups list upon response.
   * Handles both success and error scenarios by updating the loading state and result list.
   *
   * @param q - The query string to search for managers. If empty, the search is not performed.
   */
  searchManagers(q: string = ''): void {
    if (!q) return;

    this.searchManagerLoading = true;
    this.contractControlService.getManagers(q).subscribe({
      next: (response) => {
        this.searchManagerLoading = false;
        this.managersAndGroups = response;
      },
      error: () => {
        this.searchManagerLoading = false;
        this.managersAndGroups = [];
      }
    });
  }

  /**
   * Handles the selection event for a manager or group manager.
   *
   * @param $item - The selected item, which can be either a group manager or a user manager.
   *
   * This method checks if the selected manager or group manager already exists in the current list of managers.
   * If it exists, it shows an error message using the global service's toastr.
   * If it does not exist, it adds the new manager to the existing list by calling the `connectManager` method.
   *
   * The method uses RxJS `pipe` and `take` operators to take only the first emission from the `managers$` observable.
   * It then subscribes to the observable to perform the existence check and subsequent actions.
   */
  selectEvent($item: ICCGroupManagerDDLItem): void {
    this.todoForm.controls['users'].value.push(this.createUserRequestObject($item));
  }

  /**
   * Clears the input by resetting the `managersAndGroups` array to an empty array.
   * This method is typically called when the input field is cleared.
   */
  inputCleared(): void {
    this.managersAndGroups = [];
  }



  /**
   * Creates a user request object based on the provided group manager item.
   *
   * @param user - The group manager dropdown list item containing user or group information.
   * @returns An object containing the type ("groups" or "users"), the original userable item,
   *          the userable ID, and the user ID.
   */
  private createUserRequestObject(user: ICCGroupManagerDDLItem): { type: string; userable: ICCGroupManagerDDLItem; userable_id: number; user_id: number; } {
    return {
      type: user.isGroup ? "groups" : "users",
      userable: user,
      userable_id: user.id,
      user_id: user.id
    }
  }

}
